import React, { useEffect, useState } from "react";
import { useFieldArray, Controller, useFormContext } from "react-hook-form";
import CustomField from "components/CustomComponents/CustomFields/input";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IExpenseGroup, IExpenseGroupDeliveryDocuments, IExpenseGroupPaymentDocuments, IMonitoringCgsFields } from "types/monitoringCgsFields";

// ── Constants ──────────────────────────────────────────────────────────────────

const WINDOW_SIZE = 3; // how many columns are visible at once

// ── Row definitions ────────────────────────────────────────────────────────────

const DELIVERY_ROWS: { key: keyof IExpenseGroupDeliveryDocuments; label: string }[] = [
  { key: "waybillAmount",           label: "სასაქონლო ზედნადები" },
  { key: "invoiceAmount",           label: "ანგარიშ-ფაქტურა" },
  { key: "acceptanceActAmount",     label: "მიღება-ჩაბარების აქტი" },
  { key: "taxDocumentAmount",       label: "საგადასახადო დოკუმენტი" },
  { key: "serviceContractAmount",   label: "მომსახურების ხელშეკრულება" },
  { key: "commercialInvoiceAmount", label: "ინვოისი" },
  { key: "purchaseActAmount",       label: "შესყიდვის აქტი" },
];

const PAYMENT_ROWS: { key: keyof IExpenseGroupPaymentDocuments; label: string }[] = [
  { key: "bankTransferAmount", label: "საბანკო გადარიცხვის ქვითარი" },
  { key: "cashReceiptAmount",  label: "სალარო აპარატის ქვითარი" },
  { key: "cardPaymentAmount",  label: "ტერმინალზე გადახდის ქვითარი" },
  { key: "paymentOrderAmount", label: "სალარო შემოსავლის ორდერი" },
  { key: "cgdAmount",          label: "ჩგდ - ჩექთან გათანაბრებული დოკუმენტი" },
];

// ── Calculation helpers ────────────────────────────────────────────────────────

function nonZeroMin(values: (number | undefined | null)[]): number {
  const positive = values.filter((v): v is number => typeof v === "number" && !isNaN(v) && v > 0);
  return positive.length > 0 ? Math.min(...positive) : 0;
}

function calcGroupAmount(group: IExpenseGroup): number {
  const d = group.deliveryDocuments ?? {};
  const p = group.paymentDocuments ?? {};
  const deliveryMin = nonZeroMin([
    d.waybillAmount, d.invoiceAmount, d.acceptanceActAmount,
    d.taxDocumentAmount, d.serviceContractAmount, d.commercialInvoiceAmount, d.purchaseActAmount,
  ]);
  const paymentMin = nonZeroMin([
    p.bankTransferAmount, p.cashReceiptAmount, p.cardPaymentAmount,
    p.paymentOrderAmount, p.cgdAmount,
  ]);
  return Math.min(deliveryMin, paymentMin);
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const S = {
  wrapper: {
    overflowX: "auto" as const,
    maxWidth: "100%",
    borderRadius: "4px",
    border: "1px solid rgba(0,0,0,0.12)",
  },
  table: {
    borderCollapse: "collapse" as const,
    minWidth: "max-content",
    width: "100%",
    tableLayout: "auto" as const,
    textAlign: "left" as const,
  },
  stickyFirstCol: {
    position: "sticky" as const,
    left: 0,
    zIndex: 2,
    background: "#fff",
    minWidth: "200px",
    maxWidth: "220px",
    boxShadow: "2px 0 4px rgba(0,0,0,0.06)",
  },
  stickyHeader: {
    position: "sticky" as const,
    top: 0,
    zIndex: 3,
    background: "#2c5b94",
    color: "#fff",
    fontWeight: 700,
    fontSize: "12px",
    padding: "8px 10px",
    textAlign: "left" as const,
    minWidth: "160px",
    whiteSpace: "nowrap" as const,
    borderBottom: "2px solid rgba(255,255,255,0.3)",
  },
  stickyHeaderFirst: {
    position: "sticky" as const,
    top: 0,
    left: 0,
    zIndex: 4,
    background: "#2c5b94",
    color: "#fff",
    fontWeight: 700,
    fontSize: "12px",
    padding: "8px 10px",
    minWidth: "200px",
    whiteSpace: "nowrap" as const,
    borderBottom: "2px solid rgba(255,255,255,0.3)",
  },
  sectionRow: {
    background: "#EAF0F8",
    fontWeight: 700,
    fontSize: "11px",
    color: "#1A3A5C",
    padding: "6px 10px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  labelCell: {
    padding: "5px 10px",
    fontSize: "12px",
    color: "rgba(0,0,0,0.7)",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
  },
  dataCell: {
    padding: "4px 6px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    textAlign: "left" as const,
  },
  calcRow: {
    background: "#D6E4F0",
    fontWeight: 700,
    fontSize: "12px",
    padding: "6px 10px",
    textAlign: "left" as const,
    borderTop: "2px solid #2c5b94",
  },
  totalRow: {
    background: "#2c5b94",
    color: "#fff",
    fontWeight: 700,
    fontSize: "13px",
    padding: "8px 10px",
  },
  input: {
    width: "70px",
    padding: "4px 6px",
    fontSize: "12px",
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: "4px",
    textAlign: "left" as const,
    outline: "none",
  },
  titleInput: {
    width: "100%",
    padding: "4px 6px",
    fontSize: "12px",
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: "4px",
    outline: "none",
    boxSizing: "border-box" as const,
  },
  output: {
    display: "block" as const,
    padding: "4px 8px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1A3A5C",
    textAlign: "left" as const,
  },
};

// ── Empty group factory ────────────────────────────────────────────────────────

function emptyGroup(): IExpenseGroup {
  return {
    title: "",
    deliveryDocuments: {
      waybillAmount: 0, invoiceAmount: 0, acceptanceActAmount: 0,
      taxDocumentAmount: 0, serviceContractAmount: 0, commercialInvoiceAmount: 0, purchaseActAmount: 0,
    },
    paymentDocuments: {
      bankTransferAmount: 0, cashReceiptAmount: 0, cardPaymentAmount: 0,
      paymentOrderAmount: 0, cgdAmount: 0,
    },
  };
}

// ── Sliding-window pagination bar ──────────────────────────────────────────────

interface PaginationBarProps {
  startIndex: number;
  totalGroups: number;
  onPrev: () => void;
  onNext: () => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({ startIndex, totalGroups, onPrev, onNext }) => {
  if (totalGroups <= WINDOW_SIZE) return null;
  const canPrev = startIndex > 0;
  const canNext = startIndex + WINDOW_SIZE < totalGroups;
  const from    = startIndex + 1;
  const to      = Math.min(startIndex + WINDOW_SIZE, totalGroups);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <IconButton size="small" onClick={onPrev} disabled={!canPrev} sx={{ color: "#2c5b94" }}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ fontSize: "12px", color: "rgba(0,0,0,0.6)", minWidth: "100px", textAlign: "center" }}>
        {from}–{to} / {totalGroups}
      </Typography>
      <IconButton size="small" onClick={onNext} disabled={!canNext} sx={{ color: "#2c5b94" }}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// ── Props ──────────────────────────────────────────────────────────────────────

interface ExpenseGroupsTableProps {
  readOnly?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────────

const ExpenseGroupsTable: React.FC<ExpenseGroupsTableProps> = ({ readOnly = false }) => {
  const { control, watch } = useFormContext<IMonitoringCgsFields>();
  const { fields, append, remove } = useFieldArray({ control, name: "expenseGroups" });

  // startIndex = first visible column (global index)
  const [startIndex, setStartIndex] = useState(0);

  const visibleFields = fields.slice(startIndex, startIndex + WINDOW_SIZE);
  const extraCols     = readOnly ? 1 : 2; // label col + "+" col (or just label col when readOnly)

  // Clamp startIndex when groups are removed so we never show empty columns
  useEffect(() => {
    const maxStart = Math.max(0, fields.length - WINDOW_SIZE);
    if (startIndex > maxStart) setStartIndex(maxStart);
  }, [fields.length, startIndex]);

  const watchedGroups: IExpenseGroup[] = watch("expenseGroups") ?? [];

  const totalConfirmedExpense = watchedGroups.reduce((sum, g) => sum + calcGroupAmount(g), 0);

  const handleAppend = () => {
    append(emptyGroup());
    // Slide window to show the new (last) column
    const newLength = fields.length + 1;
    setStartIndex(Math.max(0, newLength - WINDOW_SIZE));
  };

  const handleRemove = (globalIdx: number) => {
    remove(globalIdx);
    // Clamping is handled by the useEffect above
  };

  return (
    <Box>
      {/* ── Instruction block ─────────────────────────────────────────────── */}
      {!readOnly && (
        <Box
          sx={{
            background: "#f5f8fc",
            border: "1px solid #c5d8ee",
            borderRadius: "4px",
            padding: "10px 14px",
            marginBottom: "12px",
            fontSize: "12px",
            color: "rgba(0,0,0,0.7)",
            lineHeight: 1.6,
          }}
        >
          <strong>ინსტრუქცია: ხარჯების დაჯგუფება დოკუმენტების მიხედვით</strong>
          <br /><br />
          გთხოვთ, ცხრილის შევსებისას ხარჯები გააერთიანოთ დოკუმენტების ტიპის მიხედვით, რაც ნიშნავს, რომ არ არის
          სავალდებულო თითოეულ შესყიდვას ცალკე სვეტი დაუთმოთ. იმ შემთხვევაში, თუ რამდენიმე სხვადასხვა დანიშნულების
          შესყიდვას (მაგალითად, მასალებსა და ინვენტარს) ზუსტად ერთნაირი სახის თანმხლები დოკუმენტები აქვს (მაგ: ყველას
          ახლავს ზედნადები და სალარო აპარატის ქვითარი), ისინი უნდა მოაქციოთ ერთ სვეტში (ერთ ხარჯების ჯგუფში), ხოლო
          შესაბამის უჯრებში ჩაწეროთ ამ დოკუმენტების ჯამური თანხები.
        </Box>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <Box sx={S.wrapper}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.stickyHeaderFirst}>დოკუმენტის ტიპი</th>

              {visibleFields.map((field, localIdx) => {
                const idx = startIndex + localIdx;
                return (
                  <th key={field.id} style={S.stickyHeader}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.8)", mb: "2px" }}>
                        ჯგუფი {idx + 1}
                      </Typography>
                      {readOnly ? (
                        <span style={{ fontSize: "12px" }}>{watchedGroups[idx]?.title || "—"}</span>
                      ) : (
                        <Controller
                          name={`expenseGroups.${idx}.title`}
                          control={control}
                          render={({ field: f }) => (
                            <input {...f} value={f.value ?? ""} placeholder="სათაური" style={S.titleInput} />
                          )}
                        />
                      )}
                      {!readOnly && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(idx)}
                          sx={{ color: "rgba(255,100,100,0.85)", padding: "2px" }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      )}
                    </Box>
                  </th>
                );
              })}

              {!readOnly && (
                <th style={{ ...S.stickyHeader, background: "#1e4474", minWidth: "50px", textAlign: "center" }}>
                  <IconButton size="small" onClick={handleAppend} sx={{ color: "#fff" }}>
                    <AddIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {/* ── Delivery Documents ──────────────────────────────────── */}
            <tr>
              <td colSpan={visibleFields.length + extraCols} style={{ ...S.stickyFirstCol, ...S.sectionRow }}>
                მიწოდების დოკუმენტები
              </td>
            </tr>
            {DELIVERY_ROWS.map(({ key, label }) => (
              <tr key={key}>
                <td style={{ ...S.stickyFirstCol, ...S.labelCell }}>{label}</td>
                {visibleFields.map((field, localIdx) => {
                  const idx = startIndex + localIdx;
                  return (
                    <td key={field.id} style={S.dataCell}>
                      {readOnly ? (
                        <output style={S.output}>
                          {Number(watchedGroups[idx]?.deliveryDocuments?.[key] ?? 0)}
                        </output>
                      ) : (
                        <CustomField
                          name={`expenseGroups.${idx}.deliveryDocuments.${key}`}
                          label=""
                          type="number"
                          control={control}
                          inputSx={{ minWidth: "unset", height: "32px" }}
                        />
                      )}
                    </td>
                  );
                })}
                {!readOnly && <td style={S.dataCell} />}
              </tr>
            ))}

            {/* ── Payment Documents ───────────────────────────────────── */}
            <tr>
              <td colSpan={visibleFields.length + extraCols} style={{ ...S.stickyFirstCol, ...S.sectionRow }}>
                გადახდის დოკუმენტები
              </td>
            </tr>
            {PAYMENT_ROWS.map(({ key, label }) => (
              <tr key={key}>
                <td style={{ ...S.stickyFirstCol, ...S.labelCell }}>{label}</td>
                {visibleFields.map((field, localIdx) => {
                  const idx = startIndex + localIdx;
                  return (
                    <td key={field.id} style={S.dataCell}>
                      {readOnly ? (
                        <output style={S.output}>
                          {Number(watchedGroups[idx]?.paymentDocuments?.[key] ?? 0)}
                        </output>
                      ) : (
                        <CustomField
                          name={`expenseGroups.${idx}.paymentDocuments.${key}`}
                          label=""
                          type="number"
                          control={control}
                          inputSx={{ minWidth: "unset", height: "32px" }}
                        />
                      )}
                    </td>
                  );
                })}
                {!readOnly && <td style={S.dataCell} />}
              </tr>
            ))}

            {/* ── Calculated amount ───────────────────────────────────── */}
            {fields.length > 0 && (
              <tr>
                <td style={{ ...S.stickyFirstCol, ...S.calcRow }}>გამოთვლილი თანხა</td>
                {visibleFields.map((field, localIdx) => {
                  const idx = startIndex + localIdx;
                  return (
                    <td key={field.id} style={{ ...S.dataCell, ...S.calcRow }}>
                      <output style={S.output}>
                        {calcGroupAmount(watchedGroups[idx] ?? {})}
                      </output>
                    </td>
                  );
                })}
                {!readOnly && <td style={{ ...S.dataCell, ...S.calcRow }} />}
              </tr>
            )}

            {/* ── Grand total ─────────────────────────────────────────── */}
            {fields.length > 0 && (
              <tr>
                <td colSpan={visibleFields.length + extraCols} style={{ ...S.stickyFirstCol, ...S.totalRow }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>მთლიანი დადასტურებული ხარჯი</span>
                    <output style={{ ...S.output, color: "#fff", fontSize: "14px" }}>
                      {totalConfirmedExpense}
                    </output>
                  </Box>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {/* ── Bottom controls ────────────────────────────────────────────────── */}
      <Box sx={{ mt: "10px", display: "flex", alignItems: "center" }}>
        <PaginationBar
          startIndex={startIndex}
          totalGroups={fields.length}
          onPrev={() => setStartIndex(s => s - 1)}
          onNext={() => setStartIndex(s => s + 1)}
        />
      </Box>
    </Box>
  );
};

export default ExpenseGroupsTable;

// ── Read-only view (no react-hook-form required) ──────────────────────────────

interface ExpenseGroupsReadOnlyProps {
  expenseGroups?: IExpenseGroup[];
}

export const ExpenseGroupsReadOnly: React.FC<ExpenseGroupsReadOnlyProps> = ({
  expenseGroups = [],
}) => {
  const [startIndex, setStartIndex] = useState(0);

  const visibleGroups = expenseGroups.slice(startIndex, startIndex + WINDOW_SIZE);
  const totalConfirmedExpense = expenseGroups.reduce((sum, g) => sum + calcGroupAmount(g), 0);

  useEffect(() => {
    const maxStart = Math.max(0, expenseGroups.length - WINDOW_SIZE);
    if (startIndex > maxStart) setStartIndex(maxStart);
  }, [expenseGroups.length, startIndex]);

  return (
    <Box>
      <Box sx={S.wrapper}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.stickyHeaderFirst}>დოკუმენტის ტიპი</th>
              {visibleGroups.map((g, localIdx) => {
                const idx = startIndex + localIdx;
                return (
                  <th key={idx} style={S.stickyHeader}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                      <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.8)" }}>
                        ჯგუფი {idx + 1}
                      </Typography>
                      <span style={{ fontSize: "12px" }}>{g.title || "—"}</span>
                    </Box>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={visibleGroups.length + 1} style={{ ...S.stickyFirstCol, ...S.sectionRow }}>
                მიწოდების დოკუმენტები
              </td>
            </tr>
            {DELIVERY_ROWS.map(({ key, label }) => (
              <tr key={key}>
                <td style={{ ...S.stickyFirstCol, ...S.labelCell }}>{label}</td>
                {visibleGroups.map((g, localIdx) => (
                  <td key={startIndex + localIdx} style={S.dataCell}>
                    <output style={S.output}>{Number(g.deliveryDocuments?.[key] ?? 0)}</output>
                  </td>
                ))}
              </tr>
            ))}

            <tr>
              <td colSpan={visibleGroups.length + 1} style={{ ...S.stickyFirstCol, ...S.sectionRow }}>
                გადახდის დოკუმენტები
              </td>
            </tr>
            {PAYMENT_ROWS.map(({ key, label }) => (
              <tr key={key}>
                <td style={{ ...S.stickyFirstCol, ...S.labelCell }}>{label}</td>
                {visibleGroups.map((g, localIdx) => (
                  <td key={startIndex + localIdx} style={S.dataCell}>
                    <output style={S.output}>{Number(g.paymentDocuments?.[key] ?? 0)}</output>
                  </td>
                ))}
              </tr>
            ))}

            {expenseGroups.length > 0 && (
              <>
                <tr>
                  <td style={{ ...S.stickyFirstCol, ...S.calcRow }}>გამოთვლილი თანხა</td>
                  {visibleGroups.map((g, localIdx) => (
                    <td key={startIndex + localIdx} style={{ ...S.dataCell, ...S.calcRow }}>
                      <output style={S.output}>{calcGroupAmount(g)}</output>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={visibleGroups.length + 1} style={{ ...S.stickyFirstCol, ...S.totalRow }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>მთლიანი დადასტურებული ხარჯი</span>
                      <output style={{ ...S.output, color: "#fff", fontSize: "14px" }}>
                        {totalConfirmedExpense}
                      </output>
                    </Box>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Box>

      <Box sx={{ mt: "8px", display: "flex", justifyContent: "flex-end" }}>
        <PaginationBar
          startIndex={startIndex}
          totalGroups={expenseGroups.length}
          onPrev={() => setStartIndex(s => s - 1)}
          onNext={() => setStartIndex(s => s + 1)}
        />
      </Box>
    </Box>
  );
};
